// authStorage.js
// Simplified: single token key ('token') for all audiences.
// Audience isolation was reverted because the Android APK cannot be updated immediately
// and the Capacitor-bundled index.html lacked the meta[name="app-portal"] tag needed
// for audience detection. Restore full isolation after the next APK release.
//
// 2026-07-25 C3 auth 翻新：token 儲存改用 @capacitor/preferences（web/native 同一套 API，
// 見 docs/specs/2026-07-25-auth-refresh-spec.md 第 4.1 節）。所有 token 讀寫函式因此改為
// async；呼叫端已知清單：AuthContext.jsx（initAuth/login/logout/refreshAccessToken）、
// services/api.js（request/response 攔截器）、context/GPSContext.jsx（背景上傳原生設定，
// 本來就在 async 函式內，僅補一個 await）、layouts/AdminLayout.jsx（switchInstitution 換票，
// 同樣本來就在 async 函式內）。Redirect 相關函式維持同步 + sessionStorage，不在本次遷移範圍。

import { Preferences } from '@capacitor/preferences';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const REDIRECT_KEY = 'post_login_redirect';

export const isParentRole = (role) => role === 'parent';

export const getAudienceForRole = (role) => (isParentRole(role) ? 'parent' : 'staff');

export const getPortalVariant = () => {
    if (typeof document === 'undefined') return null;
    return document.querySelector('meta[name="app-portal"]')?.getAttribute('content') || null;
};

export const getActiveAudience = () => {
    const variant = getPortalVariant();
    if (variant === 'parent' || variant === 'staff') return variant;
    if (typeof window === 'undefined') return 'staff';
    const path = window.location.pathname || '/';
    return ['/parent', '/pickup'].some((p) => path.startsWith(p)) ? 'parent' : 'staff';
};

// All token operations use the single 'token' key regardless of audience
export const getTokenStorageKey = () => TOKEN_KEY;

export const getRedirectStorageKey = () => REDIRECT_KEY;

// 冪等的「取得目前 access token」入口（spec 4.2 四步驟）：
//   1. Preferences 有值 → 直接用（已搬遷過，或本來就是新裝置）
//   2. 沒有 → 查舊 localStorage['token']
//   3. 舊值存在 → 搬進 Preferences、清掉舊 localStorage、視同步驟 1 的結果回傳
//   4. 兩邊都沒有 → 真的未登入，回傳空字串
// 因為第一步永遠先查 Preferences、且搬遷後舊 key 會被清掉，這個函式本身就是冪等的，
// 可以放心在任何地方（不只 initAuth 啟動時）當作讀取 token 的唯一入口，
// 不需要額外協調「必須等 initAuth 先跑過一次搬遷」的呼叫順序。
export const getStoredTokenForAudience = async () => {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    if (value) return value;

    if (typeof localStorage === 'undefined') return '';
    const legacyValue = localStorage.getItem(TOKEN_KEY);
    if (legacyValue) {
        await Preferences.set({ key: TOKEN_KEY, value: legacyValue });
        localStorage.removeItem(TOKEN_KEY);
        return legacyValue;
    }
    return '';
};

export const setStoredTokenForRole = async (token) => {
    await Preferences.set({ key: TOKEN_KEY, value: token });
};

// --- Refresh token（全新 key，無需搬遷邏輯：舊版單 token 使用者本來就沒有這顆，
//     getStoredRefreshToken() 對他們而言永遠回傳空字串，攔截器會因此直接走 nuclear 登出，
//     這正是「舊使用者行為不變」的硬要求，見 spec 4.3） ---
export const getStoredRefreshToken = async () => {
    const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });
    return value || '';
};

export const setStoredRefreshToken = async (token) => {
    await Preferences.set({ key: REFRESH_TOKEN_KEY, value: token });
};

// 登出／refresh 失敗時兩顆一起清（access + refresh）。順手防呆清一次舊 localStorage key，
// 涵蓋「搬遷尚未發生就登出」這種理論上不會發生但清掉無害的邊界情況。
export const clearStoredAuthTokens = async () => {
    await Promise.all([
        Preferences.remove({ key: TOKEN_KEY }),
        Preferences.remove({ key: REFRESH_TOKEN_KEY }),
    ]);
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(TOKEN_KEY);
    }
};

export const getStoredRedirectForAudience = () => {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(REDIRECT_KEY) || null;
};

export const setStoredRedirectForAudience = (path) => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(REDIRECT_KEY, path);
};

export const clearStoredRedirectForAudience = () => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(REDIRECT_KEY);
};
